import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, Legend } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

/**
 * SentryProductionSummary Component
 * Displays Sentry production error tracking and monitoring metrics
 * Shows unresolved issues, new issues, event volume, and top issue groups
 */
const SentryProductionSummary = () => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sentryData, setSentryData] = useState(null);

  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color')?.trim() || '#F63040';
  const secondaryColor = getComputedStyle(document.documentElement).getPropertyValue('--secondary-color')?.trim() || '#57565B';

  // Calculate Sentry production health score (0-100)
  const healthScore = useMemo(() => {
    if (!sentryData) return null;
    
    let score = 100;
    const unresolved = sentryData.totalUnresolvedIssues || 0;
    const newIssues = sentryData.totalNewIssuesLastMonth || 0;
    const eventVolume = sentryData.eventVolumeTrend || [];
    
    // Unresolved issues penalty (logarithmic scaling)
    if (unresolved > 0) {
      const unresolvedPenalty = Math.min(40, 10 + Math.log10(unresolved + 1) * 15);
      score -= unresolvedPenalty;
    }
    
    // New issues penalty (trend is important)
    if (newIssues > 10) {
      const newIssuesPenalty = Math.min(25, (newIssues - 10) / 2);
      score -= newIssuesPenalty;
    }
    
    // Event stability bonus: if events are decreasing over time
    if (eventVolume.length >= 2) {
      const recentEvents = eventVolume.slice(-3).reduce((sum, v) => sum + (v.events || 0), 0);
      const olderEvents = eventVolume.slice(0, 3).reduce((sum, v) => sum + (v.events || 0), 0);
      
      if (recentEvents < olderEvents && olderEvents > 0) {
        const improvementRate = (olderEvents - recentEvents) / olderEvents;
        score += Math.min(15, improvementRate * 30);
      }
    }
    
    return Math.max(0, Math.min(100, Math.round(score)));
  }, [sentryData]);

  const getScoreInterpretation = (score) => {
    if (score === null) return { text: 'Insufficient Data', color: '#999', icon: '?' };
    if (score >= 85) return { text: 'Excellent', color: '#2e7d32', icon: '✓' };
    if (score >= 70) return { text: 'Good', color: '#7cb342', icon: '✓' };
    if (score >= 55) return { text: 'Fair', color: '#ffa000', icon: '⚠' };
    if (score >= 40) return { text: 'Needs Attention', color: '#ff5722', icon: '⚠' };
    return { text: 'Critical', color: '#d32f2f', icon: '✗' };
  };

  // Fetch Sentry production health data with cache-first strategy
  const fetchSentryData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    // Create AbortController with 60-second timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000);
    
    try {
      // Try cache first
      try {
        const cacheRes = await apiFetch('/api/Sentry/production-health/cache', { signal: controller.signal });
        if (cacheRes.ok && cacheRes.status !== 204) {
          const cacheData = await cacheRes.json().catch(() => null);
          if (cacheData && cacheData.data) {
            setSentryData(cacheData.data);
            setLoading(false);
            clearTimeout(timeoutId);
            return;
          }
        }
      } catch (catchError) {
          Sentry.captureException(catchError);
        // Silently fail and try live endpoint
      }

      // Fallback to live endpoint
      const response = await apiFetch('/api/Sentry/production-health', { signal: controller.signal });
      if (!response.ok) {
        setError(`Failed to load Sentry data: ${response.status}`);
        setSentryData(null);
        setLoading(false);
        clearTimeout(timeoutId);
        return;
      }
      const result = await response.json().catch(() => null);
      if (result) setSentryData(result.data);
    } catch (e) {
        Sentry.captureException(e);
      if (e.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        setError(e.message || 'Error loading Sentry data');
      }
      setSentryData(null);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { fetchSentryData(); }, [fetchSentryData]);

  // Format event volume data for chart
  const eventVolumeChartData = useMemo(() => {
    if (!sentryData?.eventVolumeTrend) return [];
    return sentryData.eventVolumeTrend.map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      events: item.events
    }));
  }, [sentryData]);

  // Severity color mapping
  const severityColors = {
    'error': '#d73a4a',
    'warning': '#bf8700',
    'fatal': '#cf222e',
    'info': '#1f6feb'
  };

  const getSeverityColor = (level) => {
    return severityColors[level?.toLowerCase()] || secondaryColor;
  };

  // Format number with space separator
  const formatNumber = (n) => (Number(n) || 0).toLocaleString('en-US').replace(/,/g, ' ');

  if (loading) {
    return (
      <div className="dashboard-card">
        <h2 style={{ marginTop: 0, marginBottom: 12 }}>Sentry Production Health</h2>
        <HexagonLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-card">
        <h2 style={{ marginTop: 0, marginBottom: 12 }}>Sentry Production Health</h2>
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (!sentryData) return null;

  const scoreInterp = getScoreInterpretation(healthScore);
  const totalUnresolved = sentryData.totalUnresolvedIssues || 0;
  const totalNew = sentryData.totalNewIssuesLastMonth || 0;
  const projects = sentryData.projects || [];

  const llmProxyText = [
    `Sentry Production Health Score: ${healthScore}/100`,
    `Total Unresolved Issues: ${totalUnresolved}`,
    `New Issues Last Month: ${totalNew}`,
    `Monitored Projects: ${projects.length}`,
    `Top Issues: ${sentryData.topIssueGroups?.length || 0}`
  ].join('\n');

  return (
    <div className="dashboard-card group-wrapper sentry-production-summary">
      <div data-llm-proxy style={{ display: 'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop: 0, marginBottom: 12 }}>Sentry Production Health</h2>

      {/* Executive Summary Banner */}
      <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${scoreInterp.color}` }}>
        <div className="exec-banner-layout">
          <div className="exec-banner-score">
            <div className="exec-banner-value" style={{ color: scoreInterp.color }}>
              {healthScore !== null ? healthScore : '—'}
              <span className="exec-banner-suffix">/ 100</span>
            </div>
            <div className="exec-banner-label" style={{ color: scoreInterp.color }}>{scoreInterp.icon} {scoreInterp.text}</div>
            <div className="exec-banner-sublabel">Production Stability Health</div>
          </div>
          <div className="exec-banner-body">
            <div className="exec-banner-text">
              <strong>What this means:</strong> Monitoring <strong>{projects.length} production project{projects.length !== 1 ? 's' : ''}</strong> with <strong>{totalUnresolved} unresolved issue{totalUnresolved !== 1 ? 's' : ''}</strong>.
              {totalNew > 0 && <span style={{ color: '#d73a4a', fontWeight: 600 }}> {totalNew} new issues discovered last month.</span>}
              {totalUnresolved === 0 && <span style={{ color: '#2e7d32', fontWeight: 600 }}> Excellent - no unresolved production errors.</span>}
              {totalUnresolved > 20 && <span style={{ color: '#d32f2f', fontWeight: 600 }}> High volume of unresolved errors affecting user experience.</span>}
            </div>
          </div>
        </div>
      </div>

      {sentryData.warning && (
        <div className="alert alert-warning" style={{ marginBottom: 16 }}>
          {sentryData.warning}
        </div>
      )}

      {/* Key Metrics */}
      <div className="metrics-row">
        <div className="dashboard-card metric-card">
          <div className="metric-label">Unresolved Issues</div>
          <div className="metric-value" style={{ color: totalUnresolved > 10 ? primaryColor : secondaryColor }}>
            {formatNumber(totalUnresolved)}
          </div>
          <div className="metric-subtitle">Needs attention</div>
        </div>
        <div className="dashboard-card metric-card">
          <div className="metric-label">New Issues (Month)</div>
          <div className="metric-value" style={{ color: totalNew > 20 ? '#d73a4a' : '#666' }}>
            {formatNumber(totalNew)}
          </div>
          <div className="metric-subtitle">Recently discovered</div>
        </div>
        <div className="dashboard-card metric-card">
          <div className="metric-label">Monitored Projects</div>
          <div className="metric-value">{projects.length}</div>
          <div className="metric-subtitle">Production apps</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="dashboard-grid compact">
        {/* Per-Project Breakdown */}
        {projects.length > 0 && (
          <div className="dashboard-card chart-card">
            <h4 className="chart-title">Per-Project Breakdown</h4>
            <div className="chart-subtitle">Issue distribution across projects</div>
            <div style={{ overflowX: 'auto', maxHeight: 280, overflowY: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ textAlign: 'left', padding: '6px', fontSize: 11 }}>Project</th>
                    <th style={{ padding: '6px', fontSize: 11 }}>Unresolved</th>
                    <th style={{ padding: '6px', fontSize: 11 }}>New (Month)</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project, idx) => (
                    <tr key={idx} style={{ borderTop: '1px solid #eee' }}>
                      <td style={{ textAlign: 'left', padding: '6px', fontSize: 11, fontWeight: 500 }}>{project.projectName}</td>
                      <td style={{ padding: '6px', fontSize: 11, textAlign: 'center', color: project.unresolvedIssues > 0 ? primaryColor : '#666' }}>
                        {formatNumber(project.unresolvedIssues)}
                      </td>
                      <td style={{ padding: '6px', fontSize: 11, textAlign: 'center' }}>{formatNumber(project.newIssuesLastMonth)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Event Volume Trend */}
        <div className="dashboard-card chart-card">
          <h4 className="chart-title">Event Volume Trend</h4>
          <div className="chart-subtitle">Error events over last 14 days</div>
          {eventVolumeChartData.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#666', fontSize: 13 }}>
              No event data available
            </div>
          ) : (
            <ResponsiveContainer width={isPrintMode ? 400 : "100%"} height={280}>
              <LineChart data={eventVolumeChartData} margin={{ top: 10, right: 10, bottom: 60, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  angle={0} 
                  textAnchor="middle" 
                  interval={Math.ceil(eventVolumeChartData.length / 7)}
                  height={60}
                  tick={{ fontSize: 10 }}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="events" 
                  stroke={primaryColor} 
                  strokeWidth={2} 
                  dot={{ r: 3 }}
                  isAnimationActive={!isPrintMode}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top 5 Issue Groups */}
      {sentryData.topIssueGroups && sentryData.topIssueGroups.length > 0 && (
        <div className="dashboard-card">
          <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Top Production Issues</h4>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>
            Most frequent problems by occurrence count — click an issue to view technical details
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table sentry-issues-table">
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ textAlign: 'left', padding: '6px', fontSize: 11 }}>Problem Description</th>
                  <th style={{ padding: '6px', fontSize: 11, whiteSpace: 'nowrap' }}>Application</th>
                  <th style={{ padding: '6px', fontSize: 11, whiteSpace: 'nowrap', width: 70 }}>Severity</th>
                  <th style={{ padding: '6px', fontSize: 11, whiteSpace: 'nowrap', width: 75 }}>Occurrences</th>
                  <th style={{ padding: '6px', fontSize: 11, whiteSpace: 'nowrap', width: 100 }}>Last Occurred</th>
                </tr>
              </thead>
              <tbody>
                {sentryData.topIssueGroups.map((issue, idx) => (
                  <tr key={idx} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ textAlign: 'left', padding: '6px', fontSize: 11 }}>
                      <div style={{ fontWeight: 500 }}>
                        {issue.friendlyTitle || issue.title}
                      </div>
                      <div className="pdf-exclude" style={{ fontSize: 9, color: '#999', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {issue.permalink ? (
                          <a href={issue.permalink} target="_blank" rel="noopener noreferrer" style={{ color: '#999', textDecoration: 'none' }}>
                            {issue.title}
                          </a>
                        ) : issue.title}
                      </div>
                    </td>
                    <td style={{ padding: '6px', fontSize: 10, whiteSpace: 'nowrap', color: '#555' }}>
                      {issue.impactArea || issue.project}
                    </td>
                    <td style={{ padding: '6px', fontSize: 11, whiteSpace: 'nowrap', textAlign: 'center' }}>
                      <span style={{ 
                        padding: '2px 8px', 
                        borderRadius: 12, 
                        background: getSeverityColor(issue.level) + '20',
                        color: getSeverityColor(issue.level),
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: 'uppercase'
                      }}>
                        {issue.level || 'N/A'}
                      </span>
                    </td>
                    <td style={{ padding: '6px', fontSize: 11, fontWeight: 700, color: primaryColor, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {formatNumber(issue.eventCount)}
                    </td>
                    <td style={{ padding: '6px', fontSize: 11, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {issue.lastSeen ? new Date(issue.lastSeen).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      }) : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Explanation Section */}
      <div className="explanation-box">
        <strong>Understanding Sentry Production Metrics:</strong>
        <ul>
          <li><strong>Production Stability Health Score:</strong> Combines unresolved issue count (40%), new issue rate (30%), and event stability trend (30%). Scores above 70 indicate stable production.</li>
          <li><strong>Unresolved Issues:</strong> Active errors not yet fixed or dismissed. High counts indicate ongoing production problems affecting users.</li>
          <li><strong>New Issues (Month):</strong> Recently discovered errors. Spikes may indicate new bugs from deployments or changing usage patterns.</li>
          <li><strong>Event Volume Trend:</strong> Number of error occurrences over time. Increasing trends suggest worsening stability; decreasing trends indicate improvement.</li>
          <li><strong>Error Levels:</strong> Fatal (critical crashes), Error (exceptions), Warning (potential issues), Info (diagnostic events).</li>
          <li><strong>Top Issue Groups:</strong> Most frequent errors by event count. High-frequency issues have largest user impact and should be prioritized.</li>
          <li><strong>Project Breakdown:</strong> Issue distribution across monitored applications. Helps identify which services need attention.</li>
          <li><strong>Monitoring Period:</strong> Sentry API limits historical data to 14 days for event trends and 30 days for issue counts.</li>
        </ul>
      </div>
    </div>
  );
};

export default SentryProductionSummary;
